import { isEqual, omit }                    from "lodash"
import { CreateSurveyQuestionRequestData }  from "types/apis/surveyInterfaces"
import { APIData }                          from "types/cms/frontendTypes"

export const SurveyQBUtils = {

  getQuestionDiff : (newQuestion : CreateSurveyQuestionRequestData,
                     oldQuestion : Partial<APIData<'api::survey-question.survey-question'>>) => {

    const diff : Partial<APIData<'api::survey-question.survey-question'>> = {}

    if (!isEqual(newQuestion.type, oldQuestion.type)) diff.type = newQuestion.type
    if (!isEqual(newQuestion.group, oldQuestion.group)) diff.group = newQuestion.group
    if (!isEqual(newQuestion.category, oldQuestion.category)) diff.category = newQuestion.category
    if (!isEqual(newQuestion.impactShc, oldQuestion.impactShc)) diff.impactShc = newQuestion.impactShc
    if (!isEqual(newQuestion.impactItemization, oldQuestion.impactItemization)) diff.impactItemization = newQuestion.impactItemization
    if (!isEqual(newQuestion.impactReco, oldQuestion.impactReco)) diff.impactReco = newQuestion.impactReco                
    if (!isEqual(newQuestion.fuelTypes, oldQuestion.fuelTypes)) diff.fuelTypes = newQuestion.fuelTypes
    if (!isEqual(newQuestion.maxLevel, oldQuestion.maxLevel)) diff.maxLevel = newQuestion.maxLevel
    if (!isEqual(newQuestion.title, oldQuestion.title)) diff.title = newQuestion.title
    if (!isEqual(newQuestion.info, oldQuestion.info)) diff.info = newQuestion.info
    if (!isEqual(newQuestion.profileCode, oldQuestion.profileCode)) diff.profileCode = newQuestion.profileCode
    // An AUTO question's stateMapper is owned by the server (derived from the links on
    // save) and the client deliberately stops sending it — the same treatment AUTO choices
    // already get below. Comparing it would pit the server's derived string against the
    // client's absent one and diff as modified on every save, pushing an undefined back.
    const isAutoQuestion = (newQuestion as any).mapperSource === 'AUTO'
    if (!isAutoQuestion && !isEqual(newQuestion.stateMapper, oldQuestion.stateMapper)) {
      diff.stateMapper = newQuestion.stateMapper
    }
    if (!isEqual(newQuestion.verificationStatus, oldQuestion.verificationStatus)) diff.verificationStatus = newQuestion.verificationStatus

    // SM-26: question-level structured mapper model. Without these here, a change to
    // the links or mapperSource would never make it into the edit-mode payload (which
    // only sends the diff) — it would look saved in the UI and silently not persist.
    // Link components carry a Strapi `id` the client doesn't, so compare content only.
    const stripLinkIds = (links: any) => Array.isArray(links) ? links.map((l: any) => omit(l, 'id')) : links
    const newQ = newQuestion as any
    const oldQ = oldQuestion as any
    if (!isEqual(newQ.mapperSource, oldQ.mapperSource)) (diff as any).mapperSource = newQ.mapperSource
    if (!isEqual(stripLinkIds(newQ.homeProfileLinks), stripLinkIds(oldQ.homeProfileLinks))) {
      (diff as any).homeProfileLinks = newQ.homeProfileLinks
    }
    if (!isEqual(stripLinkIds(newQ.applianceLinks), stripLinkIds(oldQ.applianceLinks))) {
      (diff as any).applianceLinks = newQ.applianceLinks
    }
    
    // Normalize localizations for comparison (empty array vs undefined)
    const oldLocalizations = oldQuestion.localizations || []
    const newLocalizations = newQuestion.localizations || []
    if (!isEqual(oldLocalizations, newLocalizations)) {
      // Only include if new localizations has content, otherwise omit from diff
      if (newLocalizations.length > 0) {
        diff.localizations = newLocalizations
      }
    }

    // Normalize choices for comparison - remove IDs and ensure consistent structure
    const normalizeChoices = (choices: any[] | undefined) => {
      const choicesArray = choices || [];
      return choicesArray.map((choice, index) => {
        const normalized = omit(choice, 'id');
        // Ensure order is set (use index + 1 if missing, but preserve existing order)
        if (normalized.order === undefined || normalized.order === null) {
          normalized.order = index + 1;
        }
        // SM-25: structured mapper clauses come back from Strapi with a component `id`
        // per row that clauses built in the guided builder don't carry. Strip them the
        // same way choices/subChoices already are, or every question with structured
        // mappers diffs as modified the moment it loads.
        for (const clauseField of ['hpMapperClauses', 'apMapperClauses'] as const) {
          if (Array.isArray(normalized[clauseField])) {
            normalized[clauseField] = normalized[clauseField].map((c: any) => omit(c, 'id'));
            // DA-2184: authors now write ONLY the READ intent — the BE derives write/unselect
            // on save. Compare AUTO choices on their READ clauses only, so a legacy row's
            // WRITE/UNSELECT clauses (which the client drops) never read as a false diff.
            if (normalized.mapperSource === 'AUTO') {
              normalized[clauseField] = normalized[clauseField].filter((c: any) => c.role === 'READ');
            }
          }
        }
        // Normalize subChoices
        if (normalized.subChoices && Array.isArray(normalized.subChoices)) {
          normalized.subChoices = normalized.subChoices.map((sc: any, scIndex: number) => {
            const normalizedSub = omit(sc, 'id');
            // Ensure order is set for subChoices too
            if (normalizedSub.order === undefined || normalizedSub.order === null) {
              normalizedSub.order = scIndex + 1;
            }
            return normalizedSub;
          });
        }
        // SM-26: an AUTO choice's read/write/unselect strings are owned by the server
        // (derived from the clauses on save), and the client deliberately stops sending
        // them. Compare AUTO choices on their structured content only — otherwise the
        // server's derived strings vs the client's absent ones read as a change on
        // every single save.
        if (normalized.mapperSource === 'AUTO') {
          return omit(normalized, 'readMapper', 'writeMapper', 'unselectMapper');
        }
        return normalized;
      });
    };

    const oldChoicesWithoutID = normalizeChoices(oldQuestion.choices);
    const newChoicesWithoutID = normalizeChoices(newQuestion.choices);
    
    if (!isEqual(oldChoicesWithoutID, newChoicesWithoutID)) {
      diff.choices = newChoicesWithoutID as any;
    }
    return diff
  },
}